import { useCallback, useMemo } from 'react';

import { Category } from '../../../../../shared/issuesV2';
import { Action } from '../../../../../typings/permissions';
import { useAsyncRequest } from '../../../../hooks';
import { get } from '../../../../redux/api';
import { PolicyType } from '../../../../types';
import { selectOptionFactory } from '../../../../utils';
import type { Policy } from '../../../../../shared/model-typings';
import type { SelectOption } from '../../../../types';

import useUserPermittedPolicyTypes from './useUserPermittedPolicyTypes';
import useUserPermittedPolicyTypesFromTeam from './useUserPermittedPolicyTypesFromTeam';

type PolicyCategory = Category | 'SBOM';

export type CategoryPolicyOptionsMap = Record<PolicyCategory, Array<SelectOption<number | null>>>;

export const UNSET_POLICY_OPTION = selectOptionFactory(null, 'Select policy');

const POLICIES_FALLBACK: CategoryPolicyOptionsMap = {
  [Category.Licensing]: [UNSET_POLICY_OPTION],
  [Category.Vulnerability]: [UNSET_POLICY_OPTION],
  [Category.Quality]: [UNSET_POLICY_OPTION],
  SBOM: [UNSET_POLICY_OPTION],
};

const policyTypeToIssueCategory: Partial<Record<PolicyType, PolicyCategory>> = {
  LICENSING: Category.Licensing,
  SECURITY: Category.Vulnerability,
  QUALITY: Category.Quality,
  SBOM: 'SBOM',
};

/**
 * usePolicies is a hook that fetches and returns the policies for the current organization.
 */
function usePolicies() {
  const userPolicyTypes = useUserPermittedPolicyTypes();
  // HACK ALERT: We use `Action.Set` here because we're letting Team members who are able to Set a project's policy
  // be able to see the list of policies.
  // https://teamfossa.slack.com/archives/CTN0VM59S/p1719269446707479
  const userPolicyTypesFromTeam = useUserPermittedPolicyTypesFromTeam(Action.Set);

  const fetchPolicies = useCallback(async () => {
    // If the user doesn't have permission to view any policiy categories, we return the fallback to avoid the API call.
    if (!userPolicyTypes.length && !userPolicyTypesFromTeam.length) {
      return { data: null };
    }

    return get<Policy[]>('/api/policies?omitRules=true');
  }, [userPolicyTypes, userPolicyTypesFromTeam]);

  const policiesAsyncValue = useAsyncRequest(fetchPolicies);
  const policies = useMemo(() => {
    if (!policiesAsyncValue.value) {
      return POLICIES_FALLBACK;
    }

    //  We use a reduce so that we only need to iterate over the policies once.
    return policiesAsyncValue.value.reduce<CategoryPolicyOptionsMap>((acc, policy) => {
      const policyOption = selectOptionFactory(policy.id, policy.title);
      const category = policyTypeToIssueCategory[policy.type];

      // If the policy type is for a category that this hook doesn't know or care about, return the accumulator as is.
      if (!category) {
        return acc;
      }

      return {
        ...acc,
        [category]: [...acc[category], policyOption],
      };
    }, POLICIES_FALLBACK);
  }, [policiesAsyncValue.value]);

  return {
    isLoading: policiesAsyncValue.isLoading,
    policies,
    errors: policiesAsyncValue.errors,
    shouldDisplayLicensingPolicies: userPolicyTypes.includes(PolicyType.LICENSING),
    shouldDisplaySecurityPolicies: userPolicyTypes.includes(PolicyType.SECURITY),
    shouldDisplayQualityPolicies: userPolicyTypes.includes(PolicyType.QUALITY),
    shouldDisplaySbomPolicies: userPolicyTypes.includes(PolicyType.SBOM),
  };
}

export default usePolicies;
